import { Injectable } from '@angular/core';
import { Auth, signOut, user, User } from '@angular/fire/auth';
import { AppUser, IAppUser } from '../models/app-user.model';
import { firstValueFrom, ReplaySubject, take } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { SpinnerService } from './spinner.service';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { IServerResponse, ServerResponse } from '../models/server-response.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  public loggedIn: boolean = false;
  public loadingAppUser: boolean = false;

  public currentFirebaseUser?: User;

  public currentUser?: AppUser;
  public subCurrentUser: ReplaySubject<AppUser> = new ReplaySubject<AppUser>(1);

  constructor(
    public auth: Auth,
    private http: HttpClient,
    private spinnerService: SpinnerService,
    private router: Router
  ) {
    user(this.auth).subscribe((firebaseUser: User | null) => {
      if (firebaseUser) {
        console.log('Existe usuario');

        this.loggedIn = true;
        this.currentFirebaseUser = firebaseUser;

        if(!this.currentUser && !this.loadingAppUser) {
          this.getUser();
        }

      } else {
        console.log('NO Existe usuario');
        this.loggedIn = false;
        this.currentFirebaseUser = undefined;
      }
    });
  }

  /*
   ██████╗ ███████╗████████╗███████╗██╗██████╗ ███████╗██████╗  █████╗ ███████╗███████╗██╗   ██╗███████╗███████╗██████╗
  ██╔════╝ ██╔════╝╚══██╔══╝██╔════╝██║██╔══██╗██╔════╝██╔══██╗██╔══██╗██╔════╝██╔════╝██║   ██║██╔════╝██╔════╝██╔══██╗
  ██║  ███╗█████╗     ██║   █████╗  ██║██████╔╝█████╗  ██████╔╝███████║███████╗█████╗  ██║   ██║███████╗█████╗  ██████╔╝
  ██║   ██║██╔══╝     ██║   ██╔══╝  ██║██╔══██╗██╔══╝  ██╔══██╗██╔══██║╚════██║██╔══╝  ██║   ██║╚════██║██╔══╝  ██╔══██╗
  ╚██████╔╝███████╗   ██║   ██║     ██║██║  ██║███████╗██████╔╝██║  ██║███████║███████╗╚██████╔╝███████║███████╗██║  ██║
   ╚═════╝ ╚══════╝   ╚═╝   ╚═╝     ╚═╝╚═╝  ╚═╝╚══════╝╚═════╝ ╚═╝  ╚═╝╚══════╝╚══════╝ ╚═════╝ ╚══════╝╚══════╝╚═╝  ╚═╝

  */
 /**
  * FUNCTION getFirebaseUser
  * 
  * @returns 
  */
  async getFirebaseUser(): Promise<User | null> {

    if (this.auth.currentUser) {
      this.loggedIn = true;
      this.currentFirebaseUser = this.auth.currentUser;

      return this.auth.currentUser;
    }

    const firebaseUser: User | null = await firstValueFrom(
      user(this.auth).pipe(take(1))
    );

    if (firebaseUser) {
      console.log('Existe usuario');

      this.loggedIn = true;
      this.currentFirebaseUser = firebaseUser;
    } else {
      console.log('NO Existe usuario');

      this.loggedIn = false;
      this.currentFirebaseUser = undefined;
    }

    return firebaseUser;
  }

  /*
   ██████╗ ███████╗████████╗████████╗ ██████╗ ██╗  ██╗███████╗███╗   ██╗
  ██╔════╝ ██╔════╝╚══██╔══╝╚══██╔══╝██╔═══██╗██║ ██╔╝██╔════╝████╗  ██║
  ██║  ███╗█████╗     ██║      ██║   ██║   ██║█████╔╝ █████╗  ██╔██╗ ██║
  ██║   ██║██╔══╝     ██║      ██║   ██║   ██║██╔═██╗ ██╔══╝  ██║╚██╗██║
  ╚██████╔╝███████╗   ██║      ██║   ╚██████╔╝██║  ██╗███████╗██║ ╚████║
   ╚═════╝ ╚══════╝   ╚═╝      ╚═╝    ╚═════╝ ╚═╝  ╚═╝╚══════╝╚═╝  ╚═══╝

  */
 /**
  * FUNCTION getToken
  * 
  * @returns 
  */
  async getToken(): Promise<string> {
    const firebaseUser: User | null = await this.getFirebaseUser();

    if (!firebaseUser) {
      throw new Error('No hay sesion activa: no se pudo obtener el token de autenticacion.');
    }

    const token: string = await firebaseUser.getIdToken();

    if (!token) {
      throw new Error('No se pudo obtener el token de autenticacion.');
    }

    return token;
  }

  /*
   ██████╗ ███████╗████████╗██╗   ██╗███████╗███████╗██████╗
  ██╔════╝ ██╔════╝╚══██╔══╝██║   ██║██╔════╝██╔════╝██╔══██╗
  ██║  ███╗█████╗     ██║   ██║   ██║███████╗█████╗  ██████╔╝
  ██║   ██║██╔══╝     ██║   ██║   ██║╚════██║██╔══╝  ██╔══██╗
  ╚██████╔╝███████╗   ██║   ╚██████╔╝███████║███████╗██║  ██║
   ╚═════╝ ╚══════╝   ╚═╝    ╚═════╝ ╚══════╝╚══════╝╚═╝  ╚═╝

  */
 /**
  * FUNCTION getUser
  */
  async getUser() {
    this.loadingAppUser = true;

    const urlApi = `${environment.urlApi}/account`;

    const maxAttempts: number = 3;

    let token: string | undefined;

    try {
      // El token se resuelve UNA sola vez y fuera del ciclo de reintentos:
      // si getToken() lanza es porque no hay sesion activa, y ese es un fallo
      // definitivo que no se corrige reintentando.
      token = await this.getToken();
    } catch(error) {
      console.error(
        'No se pudo obtener el token de autenticacion para GET /account.',
        error
      );
    }

    if (token) {

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {

        try {

          const response = await new Promise<ServerResponse<{ user: IAppUser }>>(
            (resolve, reject) => {
              this.http.get<IServerResponse<{ user: IAppUser }>>(
                urlApi,
                {
                  headers: {
                    "content-type": "application/json",
                    "Authorization": token!
                  }
                }
              ).subscribe({
                next: (
                    response: IServerResponse<{ user: IAppUser }>
                  ) => resolve(
                    new ServerResponse<{ user: IAppUser }>(response)
                  ),
                error: (error) => {
                  if (error.error) {
                    resolve(new ServerResponse(error.error));
                  } else {
                    reject(error);
                  }
                },
              });
            }
          );

          console.log(response);

          if(response.statusCode === 200) {
            this.currentUser = new AppUser(
              response.data?.user!
            );

            this.subCurrentUser.next(this.currentUser);

            console.log(
              this.currentUser,
            );

            break;
          }

          console.log(
            `Intento ${attempt} de ${maxAttempts} sin exito al obtener la cuenta.`
          );

        } catch(error) {
          console.log(error);
        }

        if (attempt === maxAttempts) {
          console.error(
            `No se pudo obtener la informacion del usuario despues de ${maxAttempts} intentos.`
          );
        } else {
          const delayMs: number = 1000 * attempt;

          await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
        }

      }

    }

    this.loadingAppUser = false;
  }

  /*
  ███████╗██╗ ██████╗ ███╗   ██╗ ██████╗ ██╗   ██╗████████╗
  ██╔════╝██║██╔════╝ ████╗  ██║██╔═══██╗██║   ██║╚══██╔══╝
  ███████╗██║██║  ███╗██╔██╗ ██║██║   ██║██║   ██║   ██║
  ╚════██║██║██║   ██║██║╚██╗██║██║   ██║██║   ██║   ██║
  ███████║██║╚██████╔╝██║ ╚████║╚██████╔╝╚██████╔╝   ██║
  ╚══════╝╚═╝ ╚═════╝ ╚═╝  ╚═══╝ ╚═════╝  ╚═════╝    ╚═╝

  */
 /**
  * FUNCTION signOut
  */
  async signOut() {
    this.spinnerService.loading = true;
    this.spinnerService.message = 'Cerrando sesión';

    await signOut(this.auth);

    this.currentUser = undefined;
    this.currentFirebaseUser = undefined;

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    sessionStorage.removeItem('user');
    this.loggedIn = false;
    this.router.navigate(['login']);
  }
  
}
